import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Profession } from '../models/profession.model';

const TITLES = ['Software Engineer', 'Accountmanager', 'Verpleegkundige', 'Docent', 'Marketing Specialist', 'HR Adviseur', 'Grafisch Ontwerper', 'Financial Controller'];
const SECTORS = ['ICT', 'Sales', 'Zorg', 'Onderwijs', 'Marketing', 'HR', 'Creatief', 'Financiën'];
const LEVELS = ['HBO', 'WO', 'MBO', 'HBO/WO'];
const TAGS = ['Fulltime', 'Parttime', 'Zelfstandig', 'Teamwork', 'Analytisch', 'Creatief', 'Communicatief', 'Technisch'];
const JOB_MARKET = ['Uitstekend', 'Zeer goed', 'Goed', 'Redelijk'];
const AI_IMPACT = ['Hoog', 'Gemiddeld', 'Laag'];
const INTEREST_AREAS = ['Analytisch', 'Technisch', 'Creatief', 'Sociaal', 'Ondernemend', 'Conventioneel'];
const TOP_SECTORS_LIST = ['ICT', 'Financiële dienstverlening', 'Industrie', 'Handel', 'Zakelijke dienstverlening', 'Gezondheidszorg', 'Onderwijs', 'Overheid'];

const STORAGE_KEY = 'savedBeroepen';

function generateProfessions(count: number): Profession[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    title: TITLES[i % TITLES.length],
    sector: SECTORS[i % SECTORS.length],
    level: LEVELS[i % LEVELS.length],
    tags: [TAGS[i % TAGS.length], TAGS[(i + 1) % TAGS.length], TAGS[(i + 2) % TAGS.length]],
    matchScore: Math.floor(60 + Math.random() * 40),
    educationLevel: LEVELS[i % LEVELS.length],
    jobMarketChances: JOB_MARKET[i % JOB_MARKET.length],
    futureProspects: JOB_MARKET[(i + 1) % JOB_MARKET.length],
    aiImpact: AI_IMPACT[i % AI_IMPACT.length],
    salaryRange: ['€2.500 - €3.500', '€3.500 - €5.000', '€4.000 - €6.000', '€5.000 - €7.500'][i % 4],
    genderDistribution: ['70% man / 30% vrouw', '40% man / 60% vrouw', '50% man / 50% vrouw', '20% man / 80% vrouw'][i % 4],
    interestAreas: [INTEREST_AREAS[i % INTEREST_AREAS.length], INTEREST_AREAS[(i + 1) % INTEREST_AREAS.length]],
    topSectors: [TOP_SECTORS_LIST[i % TOP_SECTORS_LIST.length], TOP_SECTORS_LIST[(i + 1) % TOP_SECTORS_LIST.length], TOP_SECTORS_LIST[(i + 2) % TOP_SECTORS_LIST.length]],
    description: `**${TITLES[i % TITLES.length]}**\n\nAls ${TITLES[i % TITLES.length].toLowerCase()} ben je verantwoordelijk voor het uitvoeren van taken binnen het vakgebied ${SECTORS[i % SECTORS.length]}. Je werkt in een dynamische omgeving en draagt bij aan de doelstellingen van de organisatie.\n\nDit beroep vraagt om expertise, probleemoplossend vermogen en samenwerking. Je blijft continu bijleren om bij te blijven met de nieuwste ontwikkelingen.\n\nJe dagelijkse werkzaamheden zijn veelzijdig en omvatten het werken met collega's, het bijdragen aan projecten en het leveren van kwaliteit. Je hebt uitstekende carrièremogelijkheden en kunt doorgroeien naar senior of leidinggevende posities.`,
    skillCategories: {
      knowledge: ['Project management', 'Data analysis', 'Strategy'],
      softSkills: ['Communication', 'Leadership', 'Teamwork', 'Problem solving'],
      languages: ['Nederlands', 'Engels'],
    },
    skillsMatch: [
      { name: 'Communication', level: 'match' as const },
      { name: 'Teamwork', level: 'match' as const },
      { name: 'Problem solving', level: 'match' as const },
    ],
    skillsClose: [
      { name: 'Project management', level: 'close' as const },
      { name: 'Data analysis', level: 'close' as const },
      { name: 'Agile', level: 'close' as const },
    ],
    skillsGap: [
      { name: 'Machine Learning', level: 'gap' as const },
      { name: 'Advanced analytics', level: 'gap' as const },
    ],
    vacatureCount: Math.floor(10 + Math.random() * 40),
    bedrijvenCount: Math.floor(3 + Math.random() * 12),
  })).sort((a, b) => b.matchScore - a.matchScore);
}

@Injectable({ providedIn: 'root' })
export class ProfessionMockService {
  private professions = generateProfessions(100);

  getProfessions(): Observable<Profession[]> {
    return of(this.professions);
  }

  getProfessionById(id: number): Observable<Profession | undefined> {
    return of(this.professions.find(p => p.id === id));
  }

  getSavedProfessions(): Observable<Profession[]> {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return of([]);
    return of((JSON.parse(saved) as Profession[]).sort((a, b) => b.matchScore - a.matchScore));
  }

  getSavedIds(): number[] {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    return (JSON.parse(saved) as Profession[]).map(p => p.id);
  }

  toggleSaveProfession(profession: Profession): boolean {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list: Profession[] = saved ? JSON.parse(saved) : [];
    const exists = list.some(p => p.id === profession.id);

    if (exists) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.filter(p => p.id !== profession.id)));
      return false;
    } else {
      list.push(profession);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    }
  }
}
